/**
 * Guide Context
 *
 * Manages the user's selected guide character and Nana wisdom state.
 * Persists selection to localStorage and syncs with user profile when logged in.
 *
 * Australian English throughout.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { GUIDE_CHARACTERS, DEFAULT_CHARACTER } from '../data/guideCharacters';
import {
  NANA_WISDOM,
  NANA_VARIANTS,
  NANA_POPUP_PROBABILITY,
  NANA_COOLDOWN_MS,
  getUnseenWisdom,
  getRandomNanaVariant
} from '../data/nanaWisdom';

const GuideContext = createContext(null);

const STORAGE_KEY = 'friendships_vibe_guide';
const NANA_STORAGE_KEY = 'friendships_vibe_nana';

export const GuideProvider = ({ children }) => {
  // Guide selection state
  const [selectedGuideId, setSelectedGuideId] = useState(null);
  const [guideLoading, setGuideLoading] = useState(true);
  const [hasCompletedSelection, setHasCompletedSelection] = useState(false);

  // Nana wisdom state
  const [nanaWisdomSeen, setNanaWisdomSeen] = useState([]);
  const [lastNanaPopupAt, setLastNanaPopupAt] = useState(null);
  const [nanaVariantId, setNanaVariantId] = useState(null);
  const [pendingNanaWisdom, setPendingNanaWisdom] = useState(null);

  // Load persisted state on mount
  useEffect(() => {
    try {
      // Load guide selection
      const savedGuide = localStorage.getItem(STORAGE_KEY);
      if (savedGuide) {
        const parsed = JSON.parse(savedGuide);
        setSelectedGuideId(parsed.guideId || DEFAULT_CHARACTER);
        setHasCompletedSelection(parsed.hasCompletedSelection || false);
      }

      // Load Nana state
      const savedNana = localStorage.getItem(NANA_STORAGE_KEY);
      if (savedNana) {
        const parsed = JSON.parse(savedNana);
        setNanaWisdomSeen(parsed.seen || []);
        setLastNanaPopupAt(parsed.lastPopup || null);
        setNanaVariantId(parsed.variant || null);
      }
    } catch (error) {
      console.error('[GuideContext] Error loading saved state:', error);
    } finally {
      setGuideLoading(false);
    }
  }, []);

  // Persist guide selection
  const saveGuideSelection = useCallback((guideId, completed = true) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        guideId,
        hasCompletedSelection: completed,
        selectedAt: new Date().toISOString()
      }));
    } catch (error) {
      console.error('[GuideContext] Error saving guide selection:', error);
    }
  }, []);

  // Persist Nana state
  const saveNanaState = useCallback((seen, lastPopup, variant) => {
    try {
      localStorage.setItem(NANA_STORAGE_KEY, JSON.stringify({
        seen,
        lastPopup,
        variant
      }));
    } catch (error) {
      console.error('[GuideContext] Error saving Nana state:', error);
    }
  }, []);

  /**
   * Select a guide character
   */
  const selectGuide = useCallback((guideId) => {
    if (!GUIDE_CHARACTERS[guideId]) {
      console.error('[GuideContext] Invalid guide ID:', guideId);
      return false;
    }

    setSelectedGuideId(guideId);
    setHasCompletedSelection(true);
    saveGuideSelection(guideId, true);

    return true;
  }, [saveGuideSelection]);

  /**
   * Get the currently selected guide object
   */
  const getSelectedGuide = useCallback(() => {
    return GUIDE_CHARACTERS[selectedGuideId] || GUIDE_CHARACTERS[DEFAULT_CHARACTER];
  }, [selectedGuideId]);

  /**
   * Check if Nana wisdom should be shown
   */
  const shouldShowNanaWisdom = useCallback(() => {
    // Check cooldown
    if (lastNanaPopupAt) {
      const timeSinceLastPopup = Date.now() - new Date(lastNanaPopupAt).getTime();
      if (timeSinceLastPopup < NANA_COOLDOWN_MS) {
        return false;
      }
    }

    // Check probability
    if (Math.random() > NANA_POPUP_PROBABILITY) {
      return false;
    }

    // Check if there's unseen wisdom
    const unseen = getUnseenWisdom(nanaWisdomSeen);
    if (!unseen) {
      return false;
    }

    return true;
  }, [lastNanaPopupAt, nanaWisdomSeen]);

  /**
   * Trigger a Nana wisdom popup check
   * Returns wisdom object if should show, null otherwise
   */
  const triggerNanaWisdom = useCallback((moduleId = null) => {
    if (!shouldShowNanaWisdom()) {
      return null;
    }

    // Get wisdom (module-specific if provided)
    let wisdom;
    if (moduleId) {
      const relevant = NANA_WISDOM.filter(
        w => w.relatedModules.includes(moduleId) && !nanaWisdomSeen.includes(w.id)
      );
      wisdom = relevant.length > 0
        ? relevant[Math.floor(Math.random() * relevant.length)]
        : getUnseenWisdom(nanaWisdomSeen);
    } else {
      wisdom = getUnseenWisdom(nanaWisdomSeen);
    }

    if (!wisdom) return null;

    // Assign Nana variant if not set
    let variant = nanaVariantId;
    if (!variant) {
      const randomVariant = getRandomNanaVariant();
      variant = randomVariant.id;
      setNanaVariantId(variant);
    }

    setPendingNanaWisdom(wisdom);
    return wisdom;
  }, [shouldShowNanaWisdom, nanaWisdomSeen, nanaVariantId]);

  /**
   * Mark Nana wisdom as seen and update state
   */
  const markNanaWisdomSeen = useCallback((wisdomId) => {
    const newSeen = [...nanaWisdomSeen, wisdomId];
    const now = new Date().toISOString();

    setNanaWisdomSeen(newSeen);
    setLastNanaPopupAt(now);
    setPendingNanaWisdom(null);

    saveNanaState(newSeen, now, nanaVariantId);
  }, [nanaWisdomSeen, nanaVariantId, saveNanaState]);

  /**
   * Get the current Nana variant
   */
  const getNanaVariant = useCallback(() => {
    return NANA_VARIANTS.find(v => v.id === nanaVariantId) || NANA_VARIANTS[0];
  }, [nanaVariantId]);

  /**
   * Reset guide selection (for testing or settings)
   */
  const resetGuideSelection = useCallback(() => {
    setSelectedGuideId(null);
    setHasCompletedSelection(false);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  /**
   * Reset all Nana state (for testing)
   */
  const resetNanaState = useCallback(() => {
    setNanaWisdomSeen([]);
    setLastNanaPopupAt(null);
    setNanaVariantId(null);
    setPendingNanaWisdom(null);
    localStorage.removeItem(NANA_STORAGE_KEY);
  }, []);

  const value = {
    // Guide state
    selectedGuideId: selectedGuideId || DEFAULT_CHARACTER,
    selectedGuide: getSelectedGuide(),
    hasCompletedSelection,
    guideLoading,

    // Guide actions
    selectGuide,
    getSelectedGuide,
    resetGuideSelection,

    // All characters for selection
    allCharacters: GUIDE_CHARACTERS,
    characterOrder: ['sage', 'jaz', 'mika', 'riley', 'alex', 'sam'],

    // Nana state
    nanaWisdomSeen,
    nanaVariant: getNanaVariant(),
    pendingNanaWisdom,

    // Nana actions
    triggerNanaWisdom,
    markNanaWisdomSeen,
    getNanaVariant,
    resetNanaState,

    // Nana data
    allNanaVariants: NANA_VARIANTS,
    allNanaWisdom: NANA_WISDOM
  };

  return (
    <GuideContext.Provider value={value}>
      {children}
    </GuideContext.Provider>
  );
};

/**
 * Hook to access guide context
 */
export const useGuide = () => {
  const context = useContext(GuideContext);
  if (!context) {
    throw new Error('useGuide must be used within a GuideProvider');
  }
  return context;
};

export default GuideContext;
